import { indexedDBService } from "@/services/storage";
import { useProcessedData } from "@/stores/data-processing";
import { useActiveDataflowId } from "@/stores/dataflow";
import { useRefreshFiles } from "@/stores/user-file";
import { logger } from "@/utils/debug";
import { Button, Flex, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useState } from "react";

export function WorkspaceModalSave({
  opened,
  onClose,
  title = "Save to My Data",
}: {
  opened: boolean;
  onClose: () => void;
  title?: string;
}) {
  const activeDataflowId = useActiveDataflowId();
  const processedData = useProcessedData(activeDataflowId || "");
  const refreshFiles = useRefreshFiles();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      datasetName: "",
    },
    validate: {
      datasetName: (value) =>
        value.trim().length === 0 ? "Dataset name is required" : null,
    },
  });

  const handleSubmit = async (values: { datasetName: string }) => {
    if (
      !processedData ||
      !processedData.data ||
      processedData.data.length === 0
    ) {
      setError(
        "No data available to save. Please ensure your workspace has processed data."
      );
      return;
    }

    if (!activeDataflowId) {
      setError("No active workspace found.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const fileId = `save_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 11)}`;

      const datasetName = values.datasetName.trim();
      const dbTableId = `user_file_${fileId}`;

      await indexedDBService.storeFileData(fileId, processedData.data, {
        fileId,
        fileName: datasetName,
        fileType: "application/json",
        fileSize: JSON.stringify(processedData.data).length,
        rowCount: processedData.metadata.rowCount,
        columnCount: processedData.metadata.columnCount,
        columns: processedData.metadata.columns,
        createdAt: new Date().toISOString(),
        db_table_id: dbTableId,
        table_name: datasetName,
        table_name_english: datasetName,
        description_original: `Saved from workspace: ${datasetName}`,
        description_english: `Saved from workspace: ${datasetName}`,
      });

      await refreshFiles();

      form.reset();
      onClose();
    } catch (err) {
      logger.error("Failed to save dataset:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save dataset. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      form.reset();
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      centered
      size="sm"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Dataset Name"
            placeholder="Enter dataset name"
            required
            {...form.getInputProps("datasetName")}
            disabled={saving}
            data-autofocus
          />
          {error && (
            <Text c="red" size="sm">
              {error}
            </Text>
          )}
          {processedData && (
            <Text size="sm" c="dimmed">
              {processedData.metadata.rowCount} rows ×{" "}
              {processedData.metadata.columnCount} columns
            </Text>
          )}
          <Flex justify="flex-end" gap="sm" pt="lg">
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              disabled={!processedData || processedData.data.length === 0}
            >
              Save
            </Button>
          </Flex>
        </Stack>
      </form>
    </Modal>
  );
}
